// @ts-strict-ignore
import playIcon from '@/assets/svg/play.svg'
import clsx from 'clsx'
import Image from 'next/image'
import { memo, useEffect } from 'react'
import type { Swiper as SwiperInstance } from 'swiper'
import 'swiper/css'
import 'swiper/css/a11y'
import 'swiper/css/free-mode'
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react'

import { Icon } from '@/shared/ui/icon'

import { getMediaUrl, ProductSliderMedia } from '../../../media'

import cl from './slider-pagination-detailed.module.scss'

type SliderPaginationDetailedProps = {
	className?: string
	media?: ProductSliderMedia[]
	activeIndex: number
	onSwiper: (swiper: SwiperInstance) => void
	setActiveIndex: (value: number) => void
}

export const SliderPaginationDetailed = memo((props: SliderPaginationDetailedProps) => {
	const { className, media = [], activeIndex, onSwiper, setActiveIndex } = props
	const paginationClasses = (index: number) =>
		clsx(cl.pagination__elContainer, {
			[cl.pagination__elContainer__active]: activeIndex === index
		})
	const swiper = useSwiper()
	const handleSwipeOnKeyDown = (e: KeyboardEvent) => {
		if (e.key === 'ArrowRight') {
			swiper?.slideNext()
		}
		if (e.key === 'ArrowLeft') {
			swiper?.slidePrev()
		}
	}

	useEffect(() => {
		document?.body?.addEventListener('keydown', handleSwipeOnKeyDown)
		return () => {
			document?.body?.removeEventListener('keydown', handleSwipeOnKeyDown)
		}
	}, [swiper]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const handleTransitionEnd = () => {
			setActiveIndex(swiper.realIndex)
		}

		swiper.on('transitionEnd', handleTransitionEnd)

		return () => {
			swiper.off('transitionEnd', handleTransitionEnd)
		}
	}, [swiper]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className={clsx(cl.pagination, className)}>
			<div className={cl.pagination__container}>
				<Swiper
					onSwiper={onSwiper}
					slidesPerView={'auto'}
					spaceBetween={16}
					initialSlide={activeIndex}
					watchSlidesProgress={true}>
					{media.map((item, i) => (
						<SwiperSlide key={i} className={cl.pagination__el}>
							<div className={paginationClasses(i)} onClick={() => setActiveIndex(i)}>
								{item.type === 'image' ? (
									<Image
										src={getMediaUrl(item.src)}
										width={63}
										height={69}
										style={{ objectFit: 'contain' }}
										alt=""
										draggable={false}
										priority={true}
										quality={100}
									/>
								) : (
									<>
										<video
											className={cl.pagination__video}
											poster={item.poster ? getMediaUrl(item.poster) : undefined}
											src={getMediaUrl(item.src)}
											preload={item.poster ? 'none' : 'metadata'}
											muted
											playsInline
										/>
										<span className={cl.pagination__videoPlay} aria-hidden="true">
											<Icon icon={playIcon} />
										</span>
									</>
								)}
							</div>
						</SwiperSlide>
					))}
				</Swiper>
			</div>
		</div>
	)
})
